import { Component, ChangeDetectorRef, OnInit, TrackByFunction } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Account, AccountSettings } from 'app/core/models';
import { ReportsService } from 'app/modules/reports/reports.service';
import { TsSnackbarService } from 'tsui';
import { EMPTY, noop, Observable, tap } from 'rxjs';
import { Actions, ofActionErrored, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { AccountActions } from '@state/account/actions';
import { take, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'saml-settings',
  templateUrl: './saml.component.html',
  styleUrls: ['./saml.component.scss'],
})
export class SamlSettingsComponent extends OnDestroyMixin implements OnInit {
  samlSettingsForm: UntypedFormGroup;
  @Select(AccountState.getAccount) account$: Observable<Account>;

  trackByFn: TrackByFunction<any> = (index) => index;

  constructor(
    private readonly reportsService: ReportsService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly tsSnackBarService: TsSnackbarService,
    private readonly store: Store,
    private readonly actions: Actions,
  ) {
    super();
    this.account$.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.reportsService.accountId = accountData.id;
    });
  }

  ngOnInit(): void {
    this.samlSettingsForm = this.formBuilder.group({
      id: [''],
      saml_setting: this.formBuilder.group({
        id: [''],
        idp_entity_id: ['', [Validators.required]],
        idp_sso_target_url: ['', [Validators.required]],
        idp_cert: ['', [Validators.required]],
        sp_entity_id: [''],
        active: [''],
        required: [''],
      }),
    });

    // Set the defaults
    this.account$
      .pipe(
        take(1),
        tap((account) => {
          this.samlSettingsForm.patchValue(account);
        }),
      )
      .subscribe(noop);

    this.loadActionListeners();
    this.changeDetectorRef.markForCheck();
  }

  updateSettings(): void {
    if (this.samlSettingsForm.invalid) return;

    const settings = this.samlSettingsForm.value as AccountSettings;

    this.store.dispatch(new AccountActions.UpdateAccount(settings));
  }

  private loadActionListeners() {
    this.actions
      .pipe(
        ofActionSuccessful(AccountActions.UpdateAccount),
        withLatestFrom(this.account$),
        untilComponentDestroyed(this),
      )
      .subscribe(([, account]) => {
        this.samlSettingsForm.reset(account);

        this.tsSnackBarService.open('Settings saved', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.UpdateAccount), untilComponentDestroyed(this))
      .subscribe((error) => {
        this.tsSnackBarService.open('Error saving settings', 'error');
        return EMPTY;
      });

    this.actions
      .pipe(ofActionSuccessful(AccountActions.UploadAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo updated successfully', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.UploadAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo upload error', 'error');
      });

    this.actions
      .pipe(ofActionSuccessful(AccountActions.RemoveAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo removed successfully', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.RemoveAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo removal error', 'error');
      });
  }
}
